import { Urls } from "./urls";
import { Requester } from "./requester";
import { StorageHelper, StorageItem } from "./storage-helper";

export class PushNotifications
{
    static async unsubscribeAndGetEndpoint(): Promise<string | null>
    {
        if ((window as any).median)
        {
            return null;
        }

        if (!("serviceWorker" in navigator))
        {
            return null;
        }

        const registration = await navigator.serviceWorker.getRegistration();
        if (!registration)
        {
            return null;
        }

        const subscription = await registration.pushManager.getSubscription();
        if (!subscription)
        {
            return null;
        }

        try
        {
            await subscription.unsubscribe();
        }
        catch
        {
            // Ignore unsubscribe errors (some browsers throw even if already unsubscribed)
        }

        StorageHelper.remove(StorageItem.arePushNotificationsConfigured);
        return subscription.endpoint;
    }

    static async ensureRegistration(webPushKey: string)
    {
        if (!("serviceWorker" in navigator) || !("Notification" in window) || !("PushManager" in window))
        {
            return;
        }

        const registration = await this.getRegistration();
        if (!registration)
        {
            return null;
        }

        if (!StorageHelper.get(StorageItem.arePushNotificationsConfigured))
        {
            let subscription = await registration.pushManager.getSubscription();
            if (subscription != null)
            {
                await this.register(subscription);
            }
            else if (Notification.permission !== "denied")
            {
                const permission = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
                if (permission === "granted")
                {
                    try
                    {
                        subscription = await registration.pushManager.subscribe({
                            userVisibleOnly: true,
                            applicationServerKey: this.urlBase64ToUint8Array(webPushKey),
                        });
                    }
                    catch (error)
                    {
                        console.error("Failed to subscribe to push notifications", error);
                    }
                    if (subscription != null)
                    {
                        await this.register(subscription);
                    }
                }
            }
        }
    }

    private static async register(subscription: PushSubscription)
    {
        await Requester.get(Urls.registerWebPushNotifications, {
            endpoint: subscription.endpoint,
            auth: PushNotifications.arrayBufferToBase64(subscription.getKey("auth")),
            p256dh: PushNotifications.arrayBufferToBase64(subscription.getKey("p256dh"))
        });
        StorageHelper.set(StorageItem.arePushNotificationsConfigured, true);
    }

    private static async getRegistration(): Promise<ServiceWorkerRegistration | null>
    {
        let registration = await navigator.serviceWorker.getRegistration();
        if (!registration)
        {
            try
            {
                registration = await navigator.serviceWorker.register("/sw.js");
            }
            catch (error)
            {
                console.error("Failed to register service worker for push notifications", error);
                return null;
            }
        }

        try
        {
            return await navigator.serviceWorker.ready;
        }
        catch (error)
        {
            console.error("Service worker did not become ready for push notifications", error);
            return registration;
        }
    }

    private static arrayBufferToBase64(buffer)
    {
        let binary = "";
        const bytes = new Uint8Array(buffer);
        for (let i = 0; i < bytes.byteLength; i++)
        {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    private static urlBase64ToUint8Array(base64String: string): Uint8Array
    {
        const padding = "=".repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i)
        {
            outputArray[i] = rawData.charCodeAt(i);
        }

        return outputArray;
    }
}
